import Fuse from "fuse.js";
import type { IFuseOptions } from "fuse.js";

import type { SearchEntry, UsageCounts } from "./types";

// Maximum number of results rendered in the dropdown.
const MAX_RESULTS = 8;

// Fuse scores within this distance of each other are treated as a tie, so
// usage count decides the order.
const USAGE_TIE_THRESHOLD = 0.05;

// Lowercase, strip Arabic/latin diacritics, fold interchangeable Arabic letter
// variants, and collapse separators to spaces so queries match regardless of
// casing, accents, punctuation, or common Arabic spelling variations.
export const normalizeText = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f\u064b-\u065f\u0670]/g, "")
    .replace(/[\u0623\u0625\u0622\u0671]/g, "\u0627")
    .replace(/\u0629/g, "\u0647")
    .replace(/\u0649/g, "\u064a")
    .replace(/[_/-]+/g, " ")
    .replace(/[^\w\u0600-\u06ff]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Both searchable forms of a value: normalized, plus space-free so
// "creditnote" and "credit note" match each other in either direction.
const searchForms = (value: string): string[] => {
  const normalized = normalizeText(value);
  return [normalized, normalized.replace(/\s/g, "")];
};

// Fuzzy index over label, keywords, and group; queries are normalized before searching.
const FUSE_OPTIONS: IFuseOptions<SearchEntry> = {
  keys: [
    { name: "label", getFn: (entry: SearchEntry) => searchForms(entry.label), weight: 2 },
    {
      name: "keywords",
      getFn: (entry: SearchEntry) => entry.keywords.filter(Boolean).flatMap(searchForms),
      weight: 1,
    },
    {
      name: "group",
      getFn: (entry: SearchEntry) => (entry.group ? searchForms(entry.group) : []),
      weight: 0.5,
    },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  includeScore: true,
  minMatchCharLength: 1,
};

export const createSearchIndex = (entries: SearchEntry[]): Fuse<SearchEntry> =>
  new Fuse(entries, FUSE_OPTIONS);

// The query's precomputed matching forms, derived once per ranking pass.
interface QueryForms {
  normalized: string;
  compact: string;
  createIntent: boolean;
}

// Lower is better: exact match (0) -> prefix (1) -> create-intent (2) -> substring (3) -> fuzzy-only (4).
// Pins literal label matches above fuzzy-only ones so short queries stay deterministic.
const matchTier = (entry: SearchEntry, query: QueryForms): number => {
  const normalizedLabel = normalizeText(entry.label);
  const compactLabel = normalizedLabel.replace(/\s/g, "");

  if (normalizedLabel === query.normalized || compactLabel === query.compact) return 0;
  if (normalizedLabel.startsWith(query.normalized) || compactLabel.startsWith(query.compact)) {
    return 1;
  }
  if (entry.kind === "create" && query.createIntent) return 2;
  if (normalizedLabel.includes(query.normalized) || compactLabel.includes(query.compact)) return 3;
  return 4;
};

// Filters entries to those fuzzy-matching the query, ranked with exact/prefix
// label matches pinned on top, then fuzzy relevance with usage counts breaking
// near-ties, capped at MAX_RESULTS.
export const rankEntries = (
  index: Fuse<SearchEntry>,
  query: string,
  usageCounts: UsageCounts = {},
): SearchEntry[] => {
  const normalized = normalizeText(query);
  if (!normalized) return [];

  const queryForms: QueryForms = {
    normalized,
    compact: normalized.replace(/\s/g, ""),
    createIntent: /^(new|create)\b/i.test(query),
  };

  return index
    .search(normalized)
    .map(({ item, score = 1 }) => ({ entry: item, tier: matchTier(item, queryForms), score }))
    .sort((first, second) => {
      if (first.tier !== second.tier) return first.tier - second.tier;

      const scoreDiff = first.score - second.score; // lower Fuse score = better match
      if (Math.abs(scoreDiff) > USAGE_TIE_THRESHOLD) return scoreDiff;

      const usageDiff = (usageCounts[second.entry.id] ?? 0) - (usageCounts[first.entry.id] ?? 0);
      if (usageDiff !== 0) return usageDiff;

      return first.entry.label.localeCompare(second.entry.label);
    })
    .slice(0, MAX_RESULTS)
    .map(({ entry }) => entry);
};
